/**
 * 브라우저에 남는 온라인 참여 상태 (TASK-2026-004 SPEC 「포함 범위」 4·6).
 *
 * 두 가지를 저장한다.
 *
 * - **세션 표식**: 어느 캠페인의 세션인지, 언제까지 재개할 수 있는지, 동의 버전은
 *   무엇인지. 세션 **토큰은 저장하지 않는다** — HttpOnly cookie에만 있고 코드가 읽지
 *   못한다. CSRF 토큰도 저장하지 않고 페이지를 열 때 `GET /api/v1/session`으로 받는다.
 *   저장소에 두면 저장소를 읽을 수 있는 코드가 쓰기 권한까지 얻는다.
 * - **전송 진행 표식**: ACK된 순번, ACK된 경로 조각, 보내다 만 batch 원문, 마지막 오류.
 *   계측 로그 자체가 이미 durable하므로(localStorage) 큐는 **자료를 복제하지 않고**
 *   "어디까지 보냈는가"만 남긴다. 보내던 batch 하나만 원문으로 들고 있는데, 그래야
 *   창이 닫혀도 같은 바이트를 같은 `batch_id`로 다시 보낼 수 있다(멱등).
 *
 * 쓰기는 **되읽어 확인한다.** 조용히 무시하는 저장소에서 "보냈다고 적었다"가 사실이
 * 아닐 수 있고, 그러면 같은 구간을 두 번 보내거나 영영 보내지 않는다.
 */

export const SESSION_KEY = 'ccatlas.online.session';
export const QUEUE_KEY = 'ccatlas.online.queue';
export const SESSION_SCHEMA = 'ccatlas.online-session/1';
export const QUEUE_SCHEMA = 'ccatlas.online-queue/1';

export interface StateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** 설문 답변 값. 서버 `contract.ts`의 `AnswerValue`와 같다. */
export type AnswerValue = string | number | boolean | null | readonly string[];

export interface OnlineSessionState {
  readonly schema: typeof SESSION_SCHEMA;
  readonly campaign_id: string;
  readonly session_ref: string;
  readonly consent_version: string;
  readonly resume_until: number;
  readonly created_at: number;
  /** 서버가 확정한 마지막 응답 version. 0이면 아직 없다. */
  readonly response_version: number;
  /** 최종 제출을 마쳤는가. 서버 확인 뒤에만 true다. */
  readonly submitted: boolean;
  /**
   * 응답 초안. 새로고침·오프라인에서 참가자가 다시 입력하지 않게 남긴다.
   *
   * 참가자 본인 기기에만 있고 서버로는 계약된 응답 API로만 간다. 최종 제출이
   * 확인되면 지운다 — 남겨 둘 이유가 없다.
   */
  readonly answers: Readonly<Record<string, AnswerValue>>;
  /** Q1은 플레이 뒤의 답으로 덮어쓰지 않는다(중앙 명세 F1-04.3). */
  readonly q1_locked: boolean;
}

export interface InflightBatch {
  readonly batch_id: string;
  /** 보낼 canonical 문자열 그대로. 다시 만들지 않는다. */
  readonly text: string;
  readonly chunk_keys: readonly string[];
  readonly last_seq: number | null;
  readonly tries: number;
  /**
   * 이 batch가 실은 저장 확인 기록의 spool ID (CR-01 A+, ADR-002 §6).
   *
   * ACK 뒤 이 ID만 spool에서 지운다. queue state와 함께 durable하므로 새로고침 뒤에도
   * 같은 바이트를 재전송하고 같은 기록만 지운다. 크기 때문에 잘린 나머지는 spool에 남는다.
   */
  readonly confirmation_ids: readonly number[];
  /**
   * 이 batch가 보고한 확인 기록 무결성 (R4-REV-001). ACK 뒤 `last_acked_integrity`가 된다.
   * 새로고침한 runtime도 같은 값을 이어받아 보고 완료를 판정한다.
   */
  readonly integrity: ConfirmationIntegrity | null;
}

/** queue state에 두는 확인 기록 무결성 한 쌍. `online/outbox.ts`의 같은 이름 타입과 모양이 같다. */
export interface ConfirmationIntegrity {
  readonly confirmations_dropped: number;
  readonly confirmations_state: 'complete' | 'loss' | 'unverified';
}

/** 모양이 계약과 맞는 무결성 쌍만 읽는다. 아니면 `null`. */
export function readIntegrity(value: unknown): ConfirmationIntegrity | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  const dropped = record['confirmations_dropped'];
  const state = record['confirmations_state'];
  if (
    typeof dropped !== 'number' ||
    !Number.isSafeInteger(dropped) ||
    dropped < 0 ||
    (state !== 'complete' && state !== 'loss' && state !== 'unverified') ||
    (state === 'complete' && dropped !== 0) ||
    (state === 'loss' && dropped === 0)
  ) {
    return null;
  }
  return { confirmations_dropped: dropped, confirmations_state: state };
}

export interface ClockSegment {
  readonly id: string;
  readonly started_at: number;
}

export interface QueueStateShape {
  readonly schema: typeof QUEUE_SCHEMA;
  readonly session_ref: string;
  readonly through_seq: number;
  readonly acked_chunks: readonly string[];
  /** 같은 키에 다른 내용이 이미 저장된 조각. 다시 보내지 않고 사실대로 보고한다. */
  readonly conflicted_chunks: readonly string[];
  readonly inflight: InflightBatch | null;
  /**
   * 서버가 ACK한 batch가 마지막으로 보고한 확인 기록 무결성 (R4-REV-001).
   *
   * 지금 spool 상태가 이보다 나쁘면 보낼 콘텐츠가 없어도 checkpoint batch를 보낸다.
   * 없으면 기본값(`complete/0`)을 받은 것으로 본다. 이전 queue state에는 없다.
   */
  readonly last_acked_integrity: ConfirmationIntegrity | null;
  /**
   * 서버가 이 세션을 완료로 확인한 순번 경계 (TASK-2026-012 CR-03).
   *
   * 완료된 세션은 더 받지 않는다(`session_completed`). 완료를 확인한 뒤 이 기기에만 생긴 더 큰
   * 순번 —— 예컨대 뒤로가기로 되살아난 게임 화면이 남긴 기록 —— 은 보낼 자료도 미전송도
   * 아니다. 지우지 않고 이 경계로 전송·집계에서 뺀다. 한 번 적으면 풀지 않는다. `null`이면
   * 아직 완료를 확인하지 못했다.
   */
  readonly sealed_through: number | null;
  readonly last_error: string | null;
  readonly last_error_at: number | null;
  readonly last_ack_at: number | null;
  /**
   * 페이지 로드마다 한 줄 남는 clock segment (중앙 명세 F1-05).
   *
   * 이벤트의 `at`은 그 페이지의 시계로 찍힌다. 새로고침·오프라인 업로드로 순서를
   * 다시 쓰지 않기 위해, 어느 구간이 어느 페이지의 시계였는지를 남긴다. 와이어
   * 계약(`ccatlas.batch/1`)에는 이 값을 실을 자리가 없으므로 로컬 진단용이다.
   */
  readonly segments: readonly ClockSegment[];
}

/** segment 기록 상한. 오래된 것부터 버린다. */
export const MAX_SEGMENTS = 50;

export function emptyQueueState(sessionRef: string): QueueStateShape {
  return {
    schema: QUEUE_SCHEMA,
    session_ref: sessionRef,
    through_seq: 0,
    acked_chunks: [],
    conflicted_chunks: [],
    inflight: null,
    last_acked_integrity: null,
    sealed_through: null,
    last_error: null,
    last_error_at: null,
    last_ack_at: null,
    segments: [],
  };
}

function readJson(storage: StateStorage, key: string): unknown {
  try {
    const raw = storage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** 적고 되읽어 확인한다. 확인하지 못하면 `false`. */
function writeJson(storage: StateStorage, key: string, value: unknown): boolean {
  const text = JSON.stringify(value);
  try {
    storage.setItem(key, text);
  } catch {
    return false;
  }
  try {
    return storage.getItem(key) === text;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function readSessionState(storage: StateStorage): OnlineSessionState | undefined {
  const value = readJson(storage, SESSION_KEY);
  if (!isRecord(value) || value['schema'] !== SESSION_SCHEMA) {
    return undefined;
  }
  const {
    campaign_id,
    session_ref,
    consent_version,
    resume_until,
    created_at,
    response_version,
    submitted,
    answers,
    q1_locked,
  } = value;
  if (
    typeof campaign_id !== 'string' ||
    typeof session_ref !== 'string' ||
    typeof consent_version !== 'string' ||
    typeof resume_until !== 'number' ||
    typeof created_at !== 'number'
  ) {
    // 모양이 깨진 표식은 없는 것으로 본다. 참가자는 다시 시작할 수 있다.
    return undefined;
  }
  return {
    schema: SESSION_SCHEMA,
    campaign_id,
    session_ref,
    consent_version,
    resume_until,
    created_at,
    response_version: typeof response_version === 'number' ? response_version : 0,
    submitted: submitted === true,
    answers: isRecord(answers) ? (answers as Record<string, AnswerValue>) : {},
    q1_locked: q1_locked === true,
  };
}

export function writeSessionState(storage: StateStorage, state: OnlineSessionState): boolean {
  return writeJson(storage, SESSION_KEY, state);
}

export function clearSessionState(storage: StateStorage): void {
  try {
    storage.removeItem(SESSION_KEY);
  } catch {
    // 지우지 못해도 상태 표시는 서버 응답을 따른다.
  }
}

export function readQueueState(storage: StateStorage, sessionRef: string): QueueStateShape {
  const value = readJson(storage, QUEUE_KEY);
  if (!isRecord(value) || value['schema'] !== QUEUE_SCHEMA || value['session_ref'] !== sessionRef) {
    /*
     * 다른 세션의 큐는 이어받지 않는다. 순번·조각 키는 세션 안에서만 뜻이 있어서,
     * 남의 표식을 물려받으면 보내지 않은 자료를 보냈다고 여기게 된다.
     */
    return emptyQueueState(sessionRef);
  }
  const inflightRaw = value['inflight'];
  const inflight =
    isRecord(inflightRaw) && typeof inflightRaw['batch_id'] === 'string' && typeof inflightRaw['text'] === 'string'
      ? {
          batch_id: inflightRaw['batch_id'],
          text: inflightRaw['text'],
          chunk_keys: stringList(inflightRaw['chunk_keys']),
          last_seq: typeof inflightRaw['last_seq'] === 'number' ? inflightRaw['last_seq'] : null,
          tries: typeof inflightRaw['tries'] === 'number' ? inflightRaw['tries'] : 0,
          // ID 목록이 없던 이전 queue state는 빈 목록으로 읽는다(ADR-002 §10).
          confirmation_ids: Array.isArray(inflightRaw['confirmation_ids'])
            ? inflightRaw['confirmation_ids'].filter((id): id is number => typeof id === 'number' && Number.isSafeInteger(id))
            : [],
          integrity: readIntegrity(inflightRaw['integrity']),
        }
      : null;
  const segments = Array.isArray(value['segments'])
    ? value['segments'].filter(
        (item): item is ClockSegment => isRecord(item) && typeof item['id'] === 'string' && typeof item['started_at'] === 'number',
      )
    : [];
  return {
    schema: QUEUE_SCHEMA,
    session_ref: sessionRef,
    through_seq: typeof value['through_seq'] === 'number' && value['through_seq'] >= 0 ? Math.floor(value['through_seq']) : 0,
    acked_chunks: stringList(value['acked_chunks']),
    conflicted_chunks: stringList(value['conflicted_chunks']),
    inflight,
    last_acked_integrity: readIntegrity(value['last_acked_integrity']),
    sealed_through:
      typeof value['sealed_through'] === 'number' && Number.isSafeInteger(value['sealed_through']) && value['sealed_through'] >= 0
        ? value['sealed_through']
        : null,
    last_error: typeof value['last_error'] === 'string' ? value['last_error'] : null,
    last_error_at: typeof value['last_error_at'] === 'number' ? value['last_error_at'] : null,
    last_ack_at: typeof value['last_ack_at'] === 'number' ? value['last_ack_at'] : null,
    segments: segments.slice(-MAX_SEGMENTS),
  };
}

export function writeQueueState(storage: StateStorage, state: QueueStateShape): boolean {
  return writeJson(storage, QUEUE_KEY, {
    ...state,
    acked_chunks: [...new Set(state.acked_chunks)].sort(),
    conflicted_chunks: [...new Set(state.conflicted_chunks)].sort(),
    segments: state.segments.slice(-MAX_SEGMENTS),
  });
}

/** 이 페이지 로드의 clock segment를 더한다. */
export function appendSegment(state: QueueStateShape, segment: ClockSegment): QueueStateShape {
  return { ...state, segments: [...state.segments, segment].slice(-MAX_SEGMENTS) };
}
